import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { fetchDocsByApproverRole, uniqueById } from '../utils/approverDocs'
import { approveWorkflowDoc, rejectWorkflowDoc } from '../utils/approvalWorkflow'
import Modal from './Modal'
import Select from 'react-select'
import { useTheme } from '../context/ThemeContext'
import EmptyState from '../assets/images/EmptyState.png'
import { toast } from 'react-toastify'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons'
import { getStatusBadgeClass } from '../utils/statusBadge'

const SUPER_ADMIN_STATUS_OPTIONS = [
    { value: 'ALL', label: 'Semua Status' },
    { value: 'Diajukan', label: 'Diajukan' },
    { value: 'Divalidasi', label: 'Divalidasi' },
    { value: 'Diproses', label: 'Diproses' },
    { value: 'Disetujui', label: 'Disetujui' },
    { value: 'Ditolak', label: 'Ditolak' },
    { value: 'Dibatalkan', label: 'Dibatalkan' }
]

// Bagian B: tampilan "semua status sekaligus" khusus Super Admin -- menggantikan
// tab pending/approved/canceled dengan 1 tabel + filter status + search (Nama,
// Unit Bisnis, Nomor Dokumen). Peran Reviewer/Validator/Admin TIDAK memakai
// komponen ini, tetap pakai tab lama di ReimbursementCheck di bawah.
const SuperAdminAllStatusTable = ({
    allData, statusFilter, setStatusFilter, search, setSearch, loading,
    selectStyles, handleApprove, handleReject, handleEditClick, handleDelete, formatDate
}) => {
    const ACTIONABLE_STATUSES = ['Diajukan', 'Divalidasi', 'Diproses']

    const filteredData = allData.filter((item) => {
        if (statusFilter?.value !== 'ALL' && item.status !== statusFilter?.value) return false

        const searchQuery = search.trim().toLowerCase()
        if (!searchQuery) return true

        const haystack = `${item.displayId || ''} ${item.user?.nama || ''} ${item.user?.unit || ''}`.toLowerCase()
        return haystack.includes(searchQuery)
    })

    return (
        <div>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 mb-4">
                <h3 className="text-xl font-medium dark:text-gray-100">Semua Pengajuan Reimbursement</h3>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    <input
                        type="text"
                        placeholder="Cari nama, unit bisnis, atau nomor dokumen..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-10 px-4 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-full sm:w-72"
                    />
                    <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={SUPER_ADMIN_STATUS_OPTIONS}
                        styles={selectStyles}
                        isSearchable={false}
                        className="w-full sm:w-44"
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                    />
                </div>
            </div>

            {loading ? (
                <Skeleton count={5} height={40} />
            ) : filteredData.length === 0 ? (
                <div className="flex justify-center">
                    <figure className="w-44 h-44">
                        <img src={EmptyState} alt="Reimbursement icon" className="w-full h-full object-contain" />
                    </figure>
                </div>
            ) : (
                <div className="w-full overflow-x-auto">
                    <div className="inline-block min-w-[1050px] w-full">
                        <table className="w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg text-sm dark:text-gray-200">
                            <thead>
                                <tr className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-100">
                                    <th className="p-2 border dark:border-gray-600 text-center w-auto">No.</th>
                                    <th className="px-4 py-2 border dark:border-gray-600">Nomor Dokumen</th>
                                    <th className="px-4 py-2 border dark:border-gray-600">Nama</th>
                                    <th className="px-4 py-2 border dark:border-gray-600">Unit Bisnis</th>
                                    <th className="px-4 py-2 border dark:border-gray-600">Kategori Reimbursement</th>
                                    <th className="px-4 py-2 border dark:border-gray-600">Jumlah</th>
                                    <th className="px-4 py-2 border dark:border-gray-600">Tanggal Pengajuan</th>
                                    <th className="p-2 border dark:border-gray-600 text-center">Status</th>
                                    <th className="p-2 border dark:border-gray-600 text-center">Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="p-2 border dark:border-gray-600 text-center w-auto">{index + 1}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600">
                                            <Link
                                                to={`/reimbursement/${item.id}`} state={{ from: '/reimbursement/cek-pengajuan' }}
                                                className="text-black dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 hover:underline cursor-pointer"
                                            >
                                                {item.displayId}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-2 border dark:border-gray-600">{item.user?.nama}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600">{item.user?.unit}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600">{item.kategori}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600">
                                            Rp{(item.totalBiaya || 0).toLocaleString('id-ID')}
                                        </td>
                                        <td className="px-4 py-2 border dark:border-gray-600">{formatDate(item.tanggalPengajuan)}</td>
                                        <td className="p-2 border dark:border-gray-600 text-center">
                                            <span className={`px-4 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
                                                {item.status || 'Tidak Diketahui'}
                                            </span>
                                        </td>
                                        <td className="p-2 border dark:border-gray-600 text-center">
                                            <div className="flex justify-center space-x-2">
                                                <button
                                                    className="rounded-full p-1 bg-blue-200 hover:bg-blue-300 text-blue-600 border-[1px] border-blue-600 flex items-center justify-center w-8 h-8"
                                                    onClick={() => handleEditClick(item)}
                                                    title="Edit"
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4" />
                                                </button>
                                                {ACTIONABLE_STATUSES.includes(item.status) && (
                                                    <>
                                                        <button
                                                            className="rounded-full p-1 bg-green-200 hover:bg-green-300 text-green-600 border-[1px] border-green-600 flex items-center justify-center w-8 h-8"
                                                            onClick={() => handleApprove(item)}
                                                            title="Approve"
                                                        >
                                                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className="rounded-full p-1 bg-red-200 hover:bg-red-300 text-red-600 border-[1px] border-red-600 flex items-center justify-center w-8 h-8"
                                                            onClick={() => handleReject(item)}
                                                            title="Reject"
                                                        >
                                                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    className="rounded-full p-1 bg-red-200 hover:bg-red-300 text-red-700 border-[1px] border-red-700 flex items-center justify-center w-8 h-8"
                                                    onClick={() => handleDelete(item)}
                                                    title="Hapus Permanen"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

const ReimbursementCheck = () => {
    const { theme } = useTheme()
    // Bagian BO: tab dipulihkan saat kembali dari halaman Detail (lihat
    // state `from`/`tab` yang dikirim lewat Link ke Detail di bawah).
    const location = useLocation()
    const [activeTab, setActiveTab] = useState(location.state?.tab || 'pending')
    const navigate = useNavigate()
    const [data, setData] = useState({ reimbursements: [] })
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [approvedData, setApprovedData] = useState({ reimbursements: [] })
    const [canceledData, setCanceledData] = useState({ reimbursements: [] })
    const [filteredApprovedData, setFilteredApprovedData] = useState({ reimbursements: [] })
    const [filteredCanceledData, setFilteredCanceledData] = useState({ reimbursements: [] })
    const [isValidatorForAny, setIsValidatorForAny] = useState(false);

    // --- State khusus tampilan "Semua Status" Super Admin (Bagian B) ---
    const [allSuperAdminData, setAllSuperAdminData] = useState([])
    const [superAdminStatusFilter, setSuperAdminStatusFilter] = useState(SUPER_ADMIN_STATUS_OPTIONS[0])
    const [superAdminSearch, setSuperAdminSearch] = useState('')
    const [showModal, setShowModal] = useState(false)
    const [modalProps, setModalProps] = useState({})
    const [loading, setLoading] = useState(true);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')
    const [selectedReport, setselectedReport] = useState(null)

    const uid = localStorage.getItem('userUid')
    const userRole = localStorage.getItem('userRole')

    // Get current date
    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = today.getMonth() + 1 // JavaScript months are 0-indexed

    const [yearOptions, setYearOptions] = useState([{ value: currentYear, label: `${currentYear}` }])

    // Set default filters with current month and year
    const [filters, setFilters] = useState({
        bulan: { value: currentMonth, label: new Intl.DateTimeFormat('id-ID', { month: 'long' }).format(today) },
        tahun: { value: currentYear, label: `${currentYear}` }
    })

    // Fungsi untuk membuka modal
    const openModal = ({ title, message, onConfirm }) => {
        setModalProps({ title, message, onConfirm })
        setShowModal(true)
    }

    // Fungsi untuk menutup modal
    const closeModal = () => {
        setShowModal(false)
    }

    const handleEditClick = (item) => {
        if (item.kategori === 'BBM') {
            navigate('/reimbursement/bbm', { state: { isEditMode: true, editData: item } });
            
        } else if (item.kategori === 'Operasional') {
            navigate('/reimbursement/operasional', { state: { isEditMode: true, editData: item } });
            
        } else if (item.kategori === 'GA/Umum' || item.kategori === 'Umum') {
            navigate('/reimbursement/umum', { state: { isEditMode: true, editData: item } });
        }
    }

    // Bagian AM: hapus permanen (Super Admin saja) -- firestore.rules sudah
    // mengizinkan `allow delete: if isSuperAdmin()` di /reimbursement/{docId}
    // sejak awal, cuma belum ada tombolnya di UI. Status apa pun (tidak
    // dibatasi ACTIONABLE_STATUSES) -- BEDA dari "Batalkan" (ubah status jadi
    // 'Dibatalkan', tetap tersimpan untuk jejak audit).
    const handleDelete = (item) => {
        openModal({
            title: 'Konfirmasi Hapus',
            message: `Apakah Anda yakin ingin MENGHAPUS PERMANEN reimbursement ${item.displayId}? Data yang sudah dihapus tidak bisa dikembalikan.`,
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, 'reimbursement', item.id))
                    setAllSuperAdminData((prev) => prev.filter((r) => r.id !== item.id))
                    toast.success('Reimbursement berhasil dihapus.')
                    closeModal()
                } catch (error) {
                    console.error('Error deleting reimbursement:', error?.code, error?.message, error)
                    toast.error('Gagal menghapus reimbursement.')
                }
            }
        })
    }

    useEffect(() => {
        const fetchUserAndReimbursements = async () => {
            setLoading(true)
            try {
                if (!uid) {
                    console.error('UID tidak ditemukan di localStorage')
                    return
                }

                let pendingReimbursements = []
                let approvedReimbursements = []
                let canceledReimbursements = []

                if (userRole === 'Super Admin') {
                    // Bagian B: Super Admin melihat SEMUA status sekaligus (1 tabel, bukan
                    // tab pending/approved/canceled -- lihat render "Semua Status" di bawah)
                    // jadi cukup 1 query tanpa filter status sama sekali, menggantikan 2 query
                    // pending/approved yang dipakai sebelumnya. Ini juga menutup celah lama:
                    // Super Admin sebelumnya tidak pernah bisa lihat status 'Ditolak' maupun
                    // 'Dibatalkan' sama sekali di file ini (tidak ada query untuk itu untuk
                    // role Super Admin). Rules isElevatedRole() di firestore.rules sudah
                    // mendukung list broad ini.
                    const allSnapshot = await getDocs(collection(db, 'reimbursement'))
                    const allDocs = allSnapshot.docs.map((doc) => ({
                        id: doc.id,
                        displayId: doc.data().displayId,
                        ...doc.data()
                    }))
                    setAllSuperAdminData(allDocs)
                } else {
                    const { byRole, all } = await fetchDocsByApproverRole('reimbursement', ['validator', 'reviewer1', 'reviewer2'], uid)

                    pendingReimbursements = uniqueById(
                        byRole.validator.filter((doc) => doc.status === 'Diajukan'),
                        byRole.reviewer1.filter((doc) => doc.status === 'Divalidasi'),
                        byRole.reviewer2.filter((doc) => doc.status === 'Diproses')
                    )

                    approvedReimbursements = all
                        .filter((doc) => ['Divalidasi', 'Diproses', 'Disetujui'].includes(doc.status))
                        .filter((doc) => {
                            const isAssignedAsValidator = doc.user.validator?.includes(uid)
                            const isAssignedAsReviewer1 = doc.user.reviewer1?.includes(uid)
                            const isAssignedAsReviewer2 = doc.user.reviewer2?.includes(uid)

                            return doc.statusHistory.some(
                                (history) =>
                                    history.actor === uid &&
                                    ((isAssignedAsValidator && history.status === 'Disetujui oleh Validator') ||
                                        (isAssignedAsReviewer1 && history.status === 'Disetujui oleh Reviewer 1') ||
                                        (isAssignedAsReviewer2 && history.status === 'Disetujui oleh Reviewer 2'))
                            )
                        })

                    canceledReimbursements = byRole.validator
                        .filter((doc) => doc.status === 'Dibatalkan')
                        .filter((doc) => doc.statusHistory.some((history) => history.status === 'Dibatalkan'))

                    setIsValidatorForAny(canceledReimbursements.length > 0);
                }

                // Sort reimbursements by date
                pendingReimbursements.sort((a, b) => {
                    const dateA = new Date(a.tanggalPengajuan)
                    const dateB = new Date(b.tanggalPengajuan)
                    return dateA - dateB
                })

                // Sort approved reimbursements by the latest statusHistory timestamp
                approvedReimbursements.sort((a, b) => {
                    if (userRole === 'Super Admin') {
                        // Untuk Super Admin, sort berdasarkan statusHistory terakhir yang approved
                        const latestA = Math.max(
                            ...a.statusHistory
                                .filter((history) => history.status.includes('Disetujui'))
                                .map((history) => new Date(history.timestamp))
                        )
                        const latestB = Math.max(
                            ...b.statusHistory
                                .filter((history) => history.status.includes('Disetujui'))
                                .map((history) => new Date(history.timestamp))
                        )
                        return latestB - latestA
                    } else {
                        // Untuk user biasa, sort berdasarkan approval mereka sendiri
                        const latestA = Math.max(
                            ...a.statusHistory
                                .filter((history) => history.actor === uid)
                                .map((history) => new Date(history.timestamp))
                        )
                        const latestB = Math.max(
                            ...b.statusHistory
                                .filter((history) => history.actor === uid)
                                .map((history) => new Date(history.timestamp))
                        )
                        return latestB - latestA
                    }
                })

                // Sort canceled reimbursements by the latest statusHistory timestamp
                canceledReimbursements.sort((a, b) => {
                    const timestampA = a.statusHistory
                        .find(history => history.status === 'Dibatalkan')?.timestamp || '';
                    const timestampB = b.statusHistory
                        .find(history => history.status === 'Dibatalkan')?.timestamp || '';
                    return new Date(timestampB) - new Date(timestampA);
                });

                const existingYears = new Set(
                    approvedReimbursements
                        .map((item) => {
                            if (userRole === 'Super Admin') {
                                // Untuk Super Admin, ambil dari semua approval
                                return item.statusHistory
                                    .filter((status) => status.status.includes('Disetujui'))
                                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]?.timestamp
                            } else {
                                // Untuk user biasa, filter berdasarkan actor
                                return item.statusHistory
                                    .filter((status) => status.actor === uid && status.status.includes('Disetujui'))
                                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]?.timestamp
                            }
                        })
                        .filter(Boolean)
                        .map((timestamp) => new Date(timestamp).getFullYear())
                )

                const updatedYearOptions = Array.from(existingYears)
                    .map((year) => ({ value: year, label: `${year}` }))
                    .sort((a, b) => b.value - a.value) // Urutkan tahun dari yang terbaru

                setYearOptions(updatedYearOptions)

                setData({ reimbursements: pendingReimbursements })
                setApprovedData({ reimbursements: approvedReimbursements })
                setCanceledData({ reimbursements: canceledReimbursements })
            } catch (error) {
                console.error('Error fetching reimbursements data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchUserAndReimbursements()
    }, [uid, userRole])

    const getAvailableTabs = () => {
        const tabs = [
            {
                id: "pending",
                label: "Perlu Ditanggapi"
            },
            {
                id: "approved",
                label: "Riwayat Persetujuan"
            }
        ];

        if (isValidatorForAny) {
            tabs.push({
                id: "canceled",
                label: "Pengajuan Dibatalkan"
            });
        }

        return tabs;
    };

    // Handle Approve -- aturan transisinya di utils/approvalRules.js (Bagian BO),
    // dipakai bersama tombol Setujui di halaman Detail.
    const handleApprove = (item) => {
        openModal({
            title: 'Konfirmasi Approve',
            message: `Apakah Anda yakin ingin menyetujui reimbursement dengan Nomor Dokumen ${item.displayId}?`,
            onConfirm: async () => {
                try {
                    const result = await approveWorkflowDoc('rbs', item)
                    if (!result) {
                        toast.error('Anda tidak berhak menyetujui dokumen ini pada tahap saat ini.')
                        return
                    }
                    const { updatedData, newStatus } = result

                    const shouldRemoveFromPending = localStorage.getItem('userRole') !== 'Super Admin' || newStatus === 'Disetujui'

                    if (shouldRemoveFromPending) {
                        setData((prevData) => ({
                            reimbursements: prevData.reimbursements.filter((r) => r.id !== item.id)
                        }))
                    } else {
                        setData((prevData) => ({
                            reimbursements: prevData.reimbursements.map((r) => (r.id === item.id ? updatedData : r))
                        }))
                    }

                    // Update approved list dengan data terbaru dari Firestore
                    setApprovedData((prevData) => {
                        const existingIndex = prevData.reimbursements.findIndex((r) => r.id === item.id)
                        const newList = [...prevData.reimbursements]

                        if (existingIndex !== -1) {
                            newList[existingIndex] = updatedData
                        } else {
                            newList.unshift(updatedData)
                        }

                        return {
                            reimbursements: newList
                        }
                    })

                    toast.success('Reimbursement berhasil disetujui')
                    closeModal()
                } catch (error) {
                    console.error('Error approving reimbursement:', error?.code, error?.message, error)
                    toast.error('Gagal menyetujui reimbursement')
                }
            }
        })
    }

    // Handle Reject
    const handleReject = (item) => {
        setselectedReport(item)
        setIsRejectModalOpen(true)
    }

    const handleCloseRejectModal = () => {
        setIsRejectModalOpen(false)
        setRejectReason('')
        setselectedReport(null)
    }

    const handleSubmitReject = async () => {
        if (!selectedReport || !rejectReason) {
            toast.warning('Harap isi alasan penolakan terlebih dahulu!')
            return
        }

        try {
            await rejectWorkflowDoc('rbs', selectedReport, rejectReason)

            // Remove the rejected item from the list
            setData(prevData => ({
                reimbursements: prevData.reimbursements.filter(r => r.id !== selectedReport.id)
            }))

            toast.success('Reimbursement berhasil ditolak')
            handleCloseRejectModal()
        } catch (error) {
            console.error('Error rejecting reimbursement:', error?.code, error?.message, error)
            toast.error(error?.message?.includes('tidak') ? error.message : 'Gagal menolak reimbursement')
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A' // Handle null/undefined
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(date)
    }

    const filterOptions = {
        bulan: [
            { value: 1, label: 'Januari' },
            { value: 2, label: 'Februari' },
            { value: 3, label: 'Maret' },
            { value: 4, label: 'April' },
            { value: 5, label: 'Mei' },
            { value: 6, label: 'Juni' },
            { value: 7, label: 'Juli' },
            { value: 8, label: 'Agustus' },
            { value: 9, label: 'September' },
            { value: 10, label: 'Oktober' },
            { value: 11, label: 'November' },
            { value: 12, label: 'Desember' }
        ]
    }

    // Filtering effect for canceled data
    useEffect(() => {
        const filterData = () => {
            const filteredCanceled = canceledData.reimbursements.filter(item => {
                // Find the cancellation entry in statusHistory
                const canceledEntry = item.statusHistory
                    .find(status => status.status === 'Dibatalkan');

                if (!canceledEntry) return false;

                const itemDate = new Date(canceledEntry.timestamp);
                const matchesMonth = filters.bulan
                    ? itemDate.getMonth() + 1 === filters.bulan.value
                    : true;
                const matchesYear = filters.tahun
                    ? itemDate.getFullYear() === filters.tahun.value
                    : true;

                return matchesMonth && matchesYear;
            });
            setFilteredCanceledData({ reimbursements: filteredCanceled });
        };
        filterData();
    }, [filters.bulan, filters.tahun, canceledData]);

    useEffect(() => {
        const filterData = () => {
            const filtered = approvedData.reimbursements.filter(item => {
                let approvedTimestamp;
                
                if (userRole === 'Super Admin') {
                    // Super Admin melihat semua approval, ambil timestamp terakhir yang approved
                    approvedTimestamp = item.statusHistory
                        .filter(status => status.status.includes('Disetujui'))
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]?.timestamp
                } else {
                    // User biasa hanya melihat approval mereka sendiri
                    approvedTimestamp = item.statusHistory
                        .filter(status => status.actor === uid && status.status.includes('Disetujui'))
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]?.timestamp
                }

                if (!approvedTimestamp) return false

                const itemDate = new Date(approvedTimestamp)
                const matchesMonth = filters.bulan
                    ? itemDate.getMonth() + 1 === filters.bulan.value
                    : true
                const matchesYear = filters.tahun
                    ? itemDate.getFullYear() === filters.tahun.value
                    : true

                return matchesMonth && matchesYear
            })
            setFilteredApprovedData({ reimbursements: filtered })
        }
        filterData()
    }, [filters.bulan, filters.tahun, approvedData, uid, userRole])

    const handleFilterChange = (field, selectedOption) => {
        setFilters((prev) => ({
            ...prev,
            [field]: selectedOption
        }))
    }

    const isDark = theme === 'dark'
    const selectStyles = {
        control: (base) => ({
            ...base,
            display: 'flex', // Menggunakan Flexbox
            alignItems: 'center', // Teks berada di tengah vertikal
            justifyContent: 'space-between', // Menjaga ikon dropdown di kanan
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            fontSize: '12px', // Ukuran teks
            height: '32px', // Tinggi field tetap
            padding: '0 4px', // Padding horizontal
            lineHeight: 'normal', // Pastikan line-height default
            '&:hover': {
                borderColor: '#3b82f6'
            },
            borderRadius: '8px' // Sudut melengkung
        }),
        singleValue: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        input: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        placeholder: (base) => ({ ...base, color: isDark ? '#9ca3af' : '#6b7280' }),
        menu: (base) => ({
            ...base,
            zIndex: 100,
            backgroundColor: isDark ? '#1f2937' : '#ffffff'
        }),
        // Bagian AX: menuPortalTarget={document.body} butuh z-index tinggi di
        // sini juga (bukan cuma di `menu`) supaya dropdown tidak tampil
        // tertutup/salah posisi kalau Select ini dirender di dalam modal lain.
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            fontSize: '12px',
            padding: '6px 12px',
            cursor: 'pointer',
            backgroundColor: isDark
                ? (state.isSelected ? '#374151' : state.isFocused ? '#2d3748' : '#1f2937')
                : base.backgroundColor,
            color: isDark ? '#f3f4f6' : base.color
        })
    }

    const FilterSelect = ({ field, label }) => {
        // For year, use the dynamically generated yearOptions
        const options = field === 'tahun' ? yearOptions : filterOptions[field]

        return (
            <Select
                value={filters[field]}
                onChange={(option) => handleFilterChange(field, option)}
                options={options}
                placeholder={label}
                className="w-full md:w-32 xl:w-40"
                styles={selectStyles}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="absolute"
            />
        )
    }

    return (
        <div className="container mx-auto py-10 md:py-8">
            <h2 className="text-xl font-medium mb-4 dark:text-gray-100">
                Cek <span className="font-bold">Pengajuan Reimbursement</span>
            </h2>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg mb-6 shadow-sm transition-colors">
                {userRole === 'Super Admin' ? (
                    <SuperAdminAllStatusTable
                        allData={allSuperAdminData}
                        statusFilter={superAdminStatusFilter}
                        setStatusFilter={setSuperAdminStatusFilter}
                        search={superAdminSearch}
                        setSearch={setSuperAdminSearch}
                        loading={loading}
                        selectStyles={selectStyles}
                        handleApprove={handleApprove}
                        handleReject={handleReject}
                        handleEditClick={handleEditClick}
                        handleDelete={handleDelete}
                        formatDate={formatDate}
                    />
                ) : (
                <>
                <div className="mb-4">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 xl:gap-24">
                        {/* Dropdown Title Section */}
                        <div className="relative flex-shrink-0">
                            <h3
                                className="text-xl font-medium dark:text-gray-100 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-2 transition-all duration-200"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            >
                                {activeTab === 'pending'
                                    ? 'Reimbursement Perlu Ditanggapi'
                                    : activeTab === 'approved'
                                        ? 'Riwayat Persetujuan Reimbursement'
                                        : 'Pengajuan Reimbursement Dibatalkan'}
                                <svg
                                    className={`w-6 h-6 md:w-5 md:h-5 transition-transform duration-200 flex-shrink-0 ${isDropdownOpen ? "rotate-180" : ""}`}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 9l7 7 7-7" />
                                </svg>
                            </h3>

                            {isDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-lg py-1 z-50 min-w-[250px] md:w-auto shadow-lg">
                                    {getAvailableTabs().map((tab) => (
                                        <div
                                            key={tab.id}
                                            className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-600 dark:text-gray-200 cursor-pointer flex items-center justify-between"
                                            onClick={() => {
                                                setActiveTab(tab.id);
                                                setIsDropdownOpen(false);
                                            }}
                                        >
                                            <span className={`${activeTab === tab.id ? "font-medium text-red-600" : ""}`}>
                                                {tab.label}
                                            </span>
                                            {activeTab === tab.id && (
                                                <svg
                                                    className="w-5 h-5 md:w-4 md:h-4 text-red-600 flex-shrink-0"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Filter controls for approved and canceled tabs */}
                        {(activeTab === 'approved' || activeTab === 'canceled') && !loading && (
                            <div className="flex space-x-2 w-full md:w-auto">
                                <FilterSelect field="bulan" label="Bulan" />
                                <FilterSelect field="tahun" label="Tahun" />
                            </div>
                        )}
                    </div>
                </div>

                {activeTab === 'pending' ? (
                    // Pending reimbursements section 
                    loading ? (
                        <Skeleton count={5} height={40} />
                    ) : data.reimbursements.length === 0 ? (
                        <div className="flex justify-center">
                            <figure className="w-44 h-44">
                                <img
                                    src={EmptyState}
                                    alt="reimbursement icon"
                                    className="w-full h-full object-contain"
                                />
                            </figure>
                        </div>
                    ) : (
                        <div className="w-full">
                            <div className="w-full overflow-x-auto">
                                <div className="inline-block min-w-[900px] w-full">
                                    <table className="w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg text-sm dark:text-gray-200">
                                        <thead>
                                            <tr className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-100">
                                                <th className="p-2 border dark:border-gray-600 text-center w-auto">No.</th>
                                                <th className="px-4 py-2 border dark:border-gray-600">Nomor Dokumen</th>
                                                <th className="px-4 py-2 border dark:border-gray-600">Nama</th>
                                                <th className="px-4 py-2 border dark:border-gray-600">Kategori Reimbursement</th>
                                                <th className="px-4 py-2 border dark:border-gray-600">Jumlah</th>
                                                <th className="px-4 py-2 border dark:border-gray-600">Tanggal Pengajuan</th>
                                                <th className="p-2 border dark:border-gray-600 text-center">Status</th>
                                                <th className="p-2 border dark:border-gray-600 text-center">Aksi</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.reimbursements.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="p-2 border dark:border-gray-600 text-center w-auto">{index + 1}</td>
                                                    <td className="px-4 py-2 border dark:border-gray-600">
                                                        <Link
                                                            to={`/reimbursement/${item.id}`} state={{ from: '/reimbursement/cek-pengajuan', tab: activeTab }}
                                                            className="text-black dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 hover:underline cursor-pointer"
                                                        >
                                                            {item.displayId}
                                                        </Link>
                                                    </td>
                                                    <td className="px-4 py-2 border dark:border-gray-600">{item.user.nama}</td>
                                                    <td className="px-4 py-2 border dark:border-gray-600">{item.kategori}</td>
                                                    <td className="px-4 py-2 border dark:border-gray-600">
                                                        Rp{item.totalBiaya.toLocaleString('id-ID')}
                                                    </td>
                                                    <td className="px-4 py-2 border dark:border-gray-600">
                                                        {formatDate(item.tanggalPengajuan)}
                                                    </td>
                                                    <td className="p-2 border dark:border-gray-600 text-center">
                                                        <span
                                                            className={`px-4 py-1 rounded-full text-xs font-medium 
                                                                ${item.status === 'Diajukan'
                                                                    ? 'bg-blue-200 text-blue-800 border-[1px] border-blue-600'
                                                                    : item.status === 'Disetujui'
                                                                        ? 'bg-green-200 text-green-800 border-[1px] border-green-600'
                                                                        : item.status === 'Diproses'
                                                                            ? 'bg-yellow-200 text-yellow-800 border-[1px] border-yellow-600'
                                                                            : item.status === 'Ditolak'
                                                                                ? 'bg-red-200 text-red-800 border-[1px] border-red-600'
                                                                                : item.status === 'Divalidasi'
                                                                                    ? 'bg-purple-200 text-purple-800 border-[1px] border-purple-600'
                                                                                    : 'bg-gray-300 text-gray-700 border-[1px] border-gray-600'
                                                                }`}
                                                        >
                                                            {item.status || 'Tidak Diketahui'}
                                                        </span>
                                                    </td>
                                                    <td className="p-2 border dark:border-gray-600 text-center">
                                                        <div className="flex justify-center items-center space-x-2">
                                                            
                                                            {/* --- TAMBAHAN TOMBOL EDIT KHUSUS SUPER ADMIN --- */}
                                                            {userRole === 'Super Admin' && (
                                                                <button
                                                                    className="rounded-full p-1 bg-blue-200 hover:bg-blue-300 text-blue-600 border-[1px] border-blue-600 flex items-center justify-center w-8 h-8"
                                                                    onClick={() => handleEditClick(item)}
                                                                    title="Edit Data"
                                                                >
                                                                    <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            {/* ----------------------------------------------- */}

                                                            <button
                                                                className="rounded-full p-1 bg-green-200 hover:bg-green-300 text-green-600 border-[1px] border-green-600 flex items-center justify-center w-8 h-8"
                                                                onClick={() => handleApprove(item)}
                                                                title="Approve"
                                                            >
                                                                <svg
                                                                    className="w-5 h-5"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M5 13l4 4L19 7"
                                                                    />
                                                                </svg>
                                                            </button>

                                                            <button
                                                                className="rounded-full p-1 bg-red-200 hover:bg-red-300 text-red-600 border-[1px] border-red-600 flex items-center justify-center w-8 h-8"
                                                                onClick={() => handleReject(item)}
                                                                title="Reject"
                                                            >
                                                                <svg
                                                                    className="w-5 h-5"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M6 18L18 6M6 6l12 12"
                                                                    />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )
                ) : activeTab === 'approved' ? (
                    // Approved reimbursements section
                    <div>
                        {loading ? (
                            <Skeleton count={5} height={40} />
                        ) : filteredApprovedData.reimbursements.length === 0 ? (
                            <div className="flex justify-center">
                                <figure className="w-44 h-44">
                                    <img
                                        src={EmptyState}
                                        alt="reimbursement icon"
                                        className="w-full h-full object-contain"
                                    />
                                </figure>
                            </div>
                        ) : (
                            <div className="w-full">
                                <div className="w-full overflow-x-auto">
                                    <div className="inline-block min-w-[1000px] w-full">
                                        <table className="min-w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg text-sm dark:text-gray-200">
                                            <thead>
                                                <tr className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-100">
                                                    <th className="p-2 border dark:border-gray-600 text-center w-auto">No.</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Nomor Dokumen</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Nama</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Kategori Reimbursement</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Jumlah</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Tanggal Pengajuan</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Tanggal Disetujui</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600 text-center">Aksi</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredApprovedData.reimbursements.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="p-2 border dark:border-gray-600 text-center w-auto">{index + 1}</td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            <Link
                                                                to={`/reimbursement/${item.id}`} state={{ from: '/reimbursement/cek-pengajuan', tab: activeTab }}
                                                                className="text-black dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 hover:underline cursor-pointer"
                                                            >
                                                                {item.displayId}
                                                            </Link>
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600">{item.user.nama}</td>
                                                        <td className="p-4 border dark:border-gray-600">{item.kategori}</td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            Rp{item.totalBiaya.toLocaleString('id-ID')}
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            {formatDate(item.tanggalPengajuan)}
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            {formatDate(
                                                                item.statusHistory
                                                                    .filter(
                                                                        (status) =>
                                                                            status.actor === uid &&
                                                                            status.status.includes('Disetujui')
                                                                    )
                                                                    .sort(
                                                                        (a, b) =>
                                                                            new Date(b.timestamp) -
                                                                            new Date(a.timestamp)
                                                                    )[0]?.timestamp
                                                            )}
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600 text-center">
                                                            <div className="flex justify-center items-center">
                                                                <button
                                                                    onClick={() => handleEditClick(item)}
                                                                    className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none"
                                                                    title="Edit Dokumen"
                                                                >
                                                                    <div className="w-8 h-8 rounded-full border border-blue-500 flex items-center justify-center bg-blue-50 hover:bg-blue-100">
                                                                        <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4" />
                                                                    </div>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    // Canceled reimbursements section
                    <div>
                        {loading ? (
                            <Skeleton count={5} height={40} />
                        ) : filteredCanceledData.reimbursements.length === 0 ? (
                            <div className="flex justify-center">
                                <figure className="w-44 h-44">
                                    <img
                                        src={EmptyState}
                                        alt="reimbursement icon"
                                        className="w-full h-full object-contain"
                                    />
                                </figure>
                            </div>
                        ) : (
                            <div className="w-full">
                                <div className="w-full overflow-x-auto">
                                    <div className="inline-block min-w-[1000px] w-full">
                                        <table className="min-w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg text-sm dark:text-gray-200">
                                            <thead>
                                                <tr className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-100">
                                                    <th className="p-2 border dark:border-gray-600 text-center w-auto">No.</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Nomor Dokumen</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Nama</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Kategori Reimbursement</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Jumlah</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Tanggal Pengajuan</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Tanggal Dibatalkan</th>
                                                    <th className="px-4 py-2 border dark:border-gray-600">Alasan Pembatalan</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredCanceledData.reimbursements.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="p-2 border dark:border-gray-600 text-center w-auto">{index + 1}</td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            <Link
                                                                to={`/reimbursement/${item.id}`} state={{ from: '/reimbursement/cek-pengajuan', tab: activeTab }}
                                                                className="text-black dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 hover:underline cursor-pointer"
                                                            >
                                                                {item.displayId}
                                                            </Link>
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600">{item.user.nama}</td>
                                                        <td className="p-4 border dark:border-gray-600">{item.kategori}</td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            Rp{item.totalBiaya.toLocaleString('id-ID')}
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            {formatDate(item.tanggalPengajuan)}
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600">
                                                            {formatDate(
                                                                item.statusHistory
                                                                    .find(status => status.status === 'Dibatalkan')
                                                                    ?.timestamp
                                                            )}
                                                        </td>
                                                        <td className="p-4 border dark:border-gray-600 truncate max-w-[150px] overflow-hidden whitespace-nowrap">{item.cancelReason || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                </>
                )}
            </div>

            <Modal
                showModal={isRejectModalOpen}
                selectedReport={selectedReport}
                cancelReason={rejectReason}
                setCancelReason={setRejectReason}
                onClose={handleCloseRejectModal}
                onConfirm={handleSubmitReject}
                title="Konfirmasi Reject"
                message={`Apakah Anda yakin ingin menolak reimbursement dengan Nomor Dokumen ${selectedReport?.displayId || 'ini'}?`}
                cancelText="Tidak"
                confirmText="Ya, Tolak"
                showCancelReason={true}
                reasonLabel='Alasan Penolakan'
                reasonPlaceholder='Masukkan alasan penolakan...'
            />

            <Modal
                showModal={showModal}
                title={modalProps.title}
                message={modalProps.message}
                onClose={closeModal}
                onConfirm={modalProps.onConfirm}
                cancelText="Batal"
                confirmText="Setujui"
            />
        </div>
    );
}

export default ReimbursementCheck